import { defineStore } from 'pinia';
import { uniqBy, pick } from 'lodash-es';
import { ref, shallowRef } from 'vue';
import { parseJSON } from '@src/utils/index';
import { useLocalStorage } from '@src/utils/storage';
import * as api from '@src/api/city';
import type { MyCityMapItem, SystemGeoItem } from '@src/api/city';
import { useStoreUser } from '@src/store/modules/user';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore index.js has no declaration file
import { DEFAULT_CITY, MAP_VIP_STATUS } from '@src/views/Index/utils/index';

/** 当前地图上下文：id/mapId 为业务主键，areaId 为行政区划主键 */
export interface CurrentCity {
    id: string | number;
    name: string;
    mapId: string | number;
    areaId: string | number;
    latlon: [number, number];
    kuJiaLe?: string | number | null;
    vipStatus?: string;
    vipVersion?: number;
    vipEndTime?: string;
    vipStartTime?: string;
    automaticPositioning?: boolean;
}

/** 地块增量拉取参数：userId => areaId => allBoundsData */
export type MapDataParamsByUserId = Record<string, Record<string, Record<string, unknown>>>;

/** 城市选择面板分组：首字母 + 该组城市列表 */
interface CityGroupItem {
    key: string;
    options: SystemGeoItem[];
}

/** 判断是否为有效的 [lng, lat] 或 [x, y] 数值对 */
const isCoordinatePair = (value: unknown): value is [number, number] =>
    Array.isArray(value) && value.length >= 2 && Number.isFinite(Number(value[0])) && Number.isFinite(Number(value[1]));

/**
 * 将接口/定位等原始数据规范为 CurrentCity。
 * 自动定位城市仅保留 vipVersion / kuJiaLe；手动城市保留 VIP 相关字段。
 */
const normalizeCurrentCity = (raw: Record<string, unknown>): CurrentCity => {
    const source = raw && typeof raw === 'object' ? raw : {};
    const centroidCoords = parseJSON(source.centroidGeo as string)?.coordinates;
    const baseCity = {
        id: (source.id ?? source.mapId ?? DEFAULT_CITY.id) as string | number,
        mapId: (source.mapId ?? source.id ?? DEFAULT_CITY.mapId) as string | number,
        name: (source.name ?? source.areaName ?? DEFAULT_CITY.name) as string,
        areaId: (source.areaId ?? DEFAULT_CITY.areaId) as string | number,
        latlon: (isCoordinatePair(source.latlon)
            ? [Number(source.latlon[0]), Number(source.latlon[1])]
            : isCoordinatePair(centroidCoords)
              ? [Number(centroidCoords[0]), Number(centroidCoords[1])]
              : [...DEFAULT_CITY.latlon]) as [number, number],
    };

    if (source.automaticPositioning) {
        return {
            ...baseCity,
            automaticPositioning: true,
            vipVersion: (source.vipVersion as number | undefined) ?? -1,
            ...(source.kuJiaLe != null ? { kuJiaLe: source.kuJiaLe as string | number | null } : {}),
        };
    }

    return {
        ...baseCity,
        ...pick(source, ['kuJiaLe', 'vipStatus', 'vipVersion', 'vipEndTime', 'vipStartTime']),
    } as CurrentCity;
};

/** 将 geo/list 按 firstWord 分组，同 mapId 去重，供 CitySelector 渲染 */
const groupCitiesByFirstWord = (cities: SystemGeoItem[]): CityGroupItem[] => {
    const groups: Record<string, SystemGeoItem[]> = {};
    cities.forEach(city => {
        const firstWord = city.firstWord;
        if (!groups[firstWord]) groups[firstWord] = [];
        groups[firstWord].push(city);
    });
    return Object.keys(groups)
        .sort()
        .map(key => ({
            key,
            options: uniqBy(groups[key], 'mapId'),
        }));
};

export const useStoreCity = defineStore('city', () => {
    // ---------------------------- 持久化状态 ----------------------------

    /** 当前城市上下文，null 表示本地尚未写入过 currentCity（首次进入） */
    const currentCity = useLocalStorage<CurrentCity | null>('currentCity', null);
    /** IP 定位结果，供超级管理员（myCityMapList 为空）或首次进入时兜底 */
    const cityByGeolocation = useLocalStorage<Partial<CurrentCity> | null>('currentCityByGeolocation', null);
    /** 地块增量拉取参数（与 IndexedDB 同步保留，登出不清理） */
    const mapDataParamsByUserId = useLocalStorage<MapDataParamsByUserId>('mapDataParamsByUserId', {});

    // ---------------------------- 内存状态（会话级） ----------------------------

    /** 城市列表 / 我的地图接口 loading */
    const isLoading = ref(false);
    /** geo/list 原始列表，供 IP 定位 adcode 匹配 */
    const cityList = shallowRef<SystemGeoItem[]>([]);
    /** 按首字母分组后的城市列表，供 CitySelector */
    const cityGroups = shallowRef<CityGroupItem[]>([]);
    /** 我的城市地图列表；超级管理员可能为空数组 */
    const myCityMapList = shallowRef<MyCityMapItem[]>([]);

    /** 城市列表是否已拉取（同会话 dedupe） */
    let isCityListFetched = false;
    let cityListFetchPromise: Promise<CityGroupItem[]> | null = null;
    /** 我的地图是否已拉取（同会话 dedupe） */
    let isMyCityMapFetched = false;
    let myCityMapFetchPromise: Promise<MyCityMapItem[]> | null = null;

    // ---------------------------- 当前城市写入 ----------------------------
    /** 写入定位城市（在 resolveCurrentCity 之前调用） */
    const setCityByGeolocation = (city: Partial<CurrentCity>) => {
        cityByGeolocation.value = city;
    };

    /** 用户主动切换城市时调用（整对象替换，对齐旧版 SET_CURRENT_CITY） */
    const setCurrentCity = (city: Partial<CurrentCity> & Record<string, unknown>) => {
        currentCity.value = normalizeCurrentCity(city as Record<string, unknown>);
    };

    // ---------------------------- 地块增量参数（mapDataParamsByUserId） ----------------------------

    /** 缓存 allSimpleList 响应，供下次增量请求（buildingModels 置 null 避免重复上传） */
    const setMapDataParamsForArea = (userId: string | number, areaId: string | number, params: Record<string, unknown>) => {
        const userKey = String(userId);
        const areaKey = String(areaId);
        mapDataParamsByUserId.value = {
            ...mapDataParamsByUserId.value,
            [userKey]: {
                ...(mapDataParamsByUserId.value[userKey] ?? {}),
                [areaKey]: params,
            },
        };
    };

    /** 读取指定 userId 下全部 areaId → 增量参数映射 */
    const getMapDataParamsByAreaId = (userId: string | number | undefined): Record<string, Record<string, unknown>> => {
        if (userId == null || userId === '') return {};
        return mapDataParamsByUserId.value[String(userId)] ?? {};
    };

    /** 读取指定用户 + areaId 的 allSimpleList 增量参数（与旧版 urlParamsData[areaId] 对应） */
    const getMapDataParamsForArea = (userId: string | number | undefined, areaId: string | number | undefined) => {
        if (userId == null || userId === '' || areaId == null || areaId === '') return undefined;
        return getMapDataParamsByAreaId(userId)[String(areaId)];
    };

    /** 列出指定 userId 下已缓存增量参数的 areaId 键 */
    const getMapDataParamsAreaIdsForUser = (userId: string | number | undefined): string[] => {
        return Object.keys(getMapDataParamsByAreaId(userId));
    };

    /** 清除指定用户下全部地块增量参数（与 IndexedDB 清缓存配合） */
    const clearMapDataParamsForUser = (userId: string | number) => {
        const userKey = String(userId);
        if (!mapDataParamsByUserId.value[userKey]) return;
        const next = { ...mapDataParamsByUserId.value };
        delete next[userKey];
        mapDataParamsByUserId.value = next;
    };

    // ---------------------------- 城市解析与列表拉取 ----------------------------

    /**
     * 根据 myCityMapList、本地 currentCity、定位城市推导并写入 currentCity。
     * 需在 getCurrentCityByGeolocation 之后调用。
     *
     * 优先级简述：
     * 1. 本地已有 currentCity 且能在 myCityMapList 中匹配 → 用 myMap 最新数据刷新
     * 2. 本地是自动定位城市且匹配失败 → 超级管理员保留；普通用户切到第一个在期城市并刷新
     * 3. 本地已有 currentCity 但不在开通列表 → 保留城市，去掉 VIP（与选择器逛未开通城市一致）
     * 4. 本地无 currentCity → 取 myMap 第一个在期/首个城市，或定位城市，或 DEFAULT_CITY
     */
    const resolveCurrentCity = () => {
        const hasStoredCity = currentCity.value != null;
        const storedCity: Partial<CurrentCity> = currentCity.value ?? {};
        const geolocationCity = cityByGeolocation.value;
        const matchedCityInMyMap = myCityMapList.value.find(item => item.id === (storedCity.mapId ?? storedCity.id));
        const firstActiveCityInMyMap = myCityMapList.value.find(item => item.vipStatus === MAP_VIP_STATUS.ACTIVE);
        const userStore = useStoreUser();

        if (hasStoredCity) {
            if (matchedCityInMyMap) {
                currentCity.value = normalizeCurrentCity({ ...matchedCityInMyMap });
                return;
            }

            if (storedCity.automaticPositioning) {
                // 自动定位城市在 myCityMapList 中无匹配
                if (userStore.user.superMan === 'Y') return;
                if (firstActiveCityInMyMap) {
                    currentCity.value = normalizeCurrentCity({ ...firstActiveCityInMyMap });
                    window.location.reload();
                }
                return;
            }

            // 不在开通列表：保留城市，去掉上一账号/开通残留的 VIP（与 CitySelector 选未开通城市一致）
            currentCity.value = normalizeCurrentCity({
                id: storedCity.id,
                mapId: storedCity.mapId,
                name: storedCity.name,
                areaId: storedCity.areaId,
                latlon: storedCity.latlon,
                ...(storedCity.kuJiaLe != null ? { kuJiaLe: storedCity.kuJiaLe } : {}),
            });
            return;
        }

        if (myCityMapList.value.length > 0) {
            const fallbackCity = firstActiveCityInMyMap ?? myCityMapList.value[0];
            currentCity.value = normalizeCurrentCity({ ...fallbackCity });
            return;
        }

        const geolocationFallback = geolocationCity && typeof geolocationCity === 'object' ? geolocationCity : {};
        currentCity.value = normalizeCurrentCity(geolocationFallback as Record<string, unknown>);
    };

    /** 当前城市 VIP 在期或超级管理员；读 resolveCurrentCity 后的 currentCity.vipStatus */
    const isMyCurCityOrSuperAdmin = () => {
        if (useStoreUser().isSuperMan()) return true;
        return currentCity.value?.vipStatus === MAP_VIP_STATUS.ACTIVE;
    };

    /** 获取全量城市列表并按首字母分组（路由守卫预加载，带缓存） */
    const fetchCityList = async () => {
        if (isCityListFetched) return cityGroups.value;
        if (cityListFetchPromise) return cityListFetchPromise;

        isLoading.value = true;
        cityListFetchPromise = api
            .postSystemGeoList()
            .then(({ data = [] }) => {
                cityList.value = data;
                cityGroups.value = groupCitiesByFirstWord(data);
                isCityListFetched = true;
                return cityGroups.value;
            })
            .finally(() => {
                isLoading.value = false;
                cityListFetchPromise = null;
            });

        return cityListFetchPromise;
    };

    /** 获取我的城市地图列表（超级管理员可能返回空数组，仍视为已加载） */
    const fetchMyCityMapList = async (userId: string) => {
        if (!userId) return myCityMapList.value;
        if (isMyCityMapFetched) return myCityMapList.value;
        if (myCityMapFetchPromise) return myCityMapFetchPromise;

        myCityMapFetchPromise = api
            .getMapCitymapMyCityMapList({ userId })
            .then(({ data = [] }) => {
                myCityMapList.value = data ?? [];
                isMyCityMapFetched = true;
                return myCityMapList.value;
            })
            .finally(() => {
                myCityMapFetchPromise = null;
            });

        return myCityMapFetchPromise;
    };

    /** 路由守卫入口：并行拉取全量城市列表与我的地图（登录后、进入首页前） */
    const initializeCityData = async (userId: string) => {
        await fetchCityList();
        await fetchMyCityMapList(userId);
    };

    /** 登出时清空内存缓存与持久化城市上下文（mapDataParamsByUserId 保留） */
    const resetCityState = () => {
        cityList.value = [];
        cityGroups.value = [];
        myCityMapList.value = [];
        currentCity.value = null;
        cityByGeolocation.value = null;
        isCityListFetched = false;
        cityListFetchPromise = null;
        isMyCityMapFetched = false;
        myCityMapFetchPromise = null;
        isLoading.value = false;
    };

    return {
        isLoading,
        cityList,
        cityGroups,
        myCityMapList,
        currentCity,
        cityByGeolocation,
        mapDataParamsByUserId,
        fetchCityList,
        fetchMyCityMapList,
        initializeCityData,
        setCityByGeolocation,
        setCurrentCity,
        setMapDataParamsForArea,
        getMapDataParamsByAreaId,
        getMapDataParamsForArea,
        getMapDataParamsAreaIdsForUser,
        clearMapDataParamsForUser,
        resolveCurrentCity,
        isMyCurCityOrSuperAdmin,
        resetCityState,
    };
});
